import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, Heading, IndicatorDot, Layout, Paragraph, Select } from '@vwfs-bronson/bronson-react';
import { assertDefined } from '../../services/common/miscUtils';
import { RootState } from '../../services/redux';
import { notesSelectors, setConversationDocumentMetadata } from '../../services/redux/features/notes.slice';
import { fetchObjectMetadata, getReferenceData, toggleUnreadStatus } from './conversationUtils';

interface ConversationHeaderProps {
  activeConversation: MilesPosNotesIntegrationService.Components.Schemas.Conversation | null;
  isUpdatingTitle: boolean;
  updateConversationTitle: (newTitle: string) => Promise<void>;
  updateConversationStatus: (
    newStatus: MilesPosNotesIntegrationService.Components.Schemas.ConversationStatus
  ) => Promise<void>;
  isUpdatingStatus: boolean;
  titleOptions: Array<{ value: string; label: string }>;
}

export const ConversationHeader: React.FunctionComponent<ConversationHeaderProps> = ({
  activeConversation,
  isUpdatingTitle,
  updateConversationTitle,
  updateConversationStatus,
  isUpdatingStatus,
  titleOptions,
}) => {
  const { t } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [editableTitle, setEditableTitle] = useState<string>('');
  const isConversationClosed = activeConversation?.status === 'CLOSE';
  const dispatch = useDispatch();
  const conversationDocumentMetadata = useSelector((state: RootState) => state.notes.conversationDocumentMetadata);
  const conversations = useSelector(notesSelectors.selectConversations);
  const hasUnreadMessages = (activeConversation?.unreadMessages ?? 0) > 0 || !activeConversation?.isSeenByCurrentUser;

  useEffect(() => {
    setIsEditingTitle(false);
    const fetchMetadata = async () => {
      dispatch(setConversationDocumentMetadata(null));

      if (activeConversation) {
        const reference = getReferenceData(activeConversation);

        if (reference?.humanReadableObjectReferenceId && reference?.objectType) {
          const metadata = await fetchObjectMetadata(reference.humanReadableObjectReferenceId, reference.objectType);

          dispatch(
            setConversationDocumentMetadata({
              entityFullName: metadata.entityFullName ?? '',
              humanReadableObjectReferenceId: metadata.humanReadableObjectReferenceId ?? '',
              objectType: reference.objectType,
              systemObjectId: metadata.systemObjectId ?? '',
            })
          );
        }
      }
    };

    fetchMetadata();
  }, [activeConversation, dispatch]);

  const onToggleUnreadClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    toggleUnreadStatus(dispatch, assertDefined(activeConversation?.conversationId), hasUnreadMessages, conversations);
  };

  return (
    <>
      <Layout className="u-mb-small">
        <Layout.Item default="1/3" s="1/1">
          <Heading level={5} className="u-mb-none">
            {hasUnreadMessages && <IndicatorDot label="" status="unread" />}
            {activeConversation?.title || t('conversationHeader:noConversationSelected')}
          </Heading>
          <Paragraph>
            {activeConversation ? conversationDocumentMetadata?.humanReadableObjectReferenceId : ''}
          </Paragraph>
        </Layout.Item>

        <Layout.Item default="2/3" s="1/1">
          <Layout.Item>
            <ButtonContainer right>
              <Paragraph className="u-text-right u-mt-xsmall u-mb-none">
                <a
                  href=""
                  className="c-notification-center__item__heading-link u-text-base"
                  onClick={onToggleUnreadClick}
                >
                  {hasUnreadMessages ? t('conversationHeader:markAsRead') : t('conversationHeader:markAsUnread')}
                </a>
              </Paragraph>
              <Button
                ariaLabel={t('conversationHeader:editTitle')}
                icon="semantic-edit"
                round
                secondary
                onClick={() => {
                  if (activeConversation) {
                    setEditableTitle(activeConversation.title || '');
                    setIsEditingTitle(true);
                  }
                  if (isEditingTitle) {
                    setIsEditingTitle(false);
                  }
                }}
                disabled={isUpdatingTitle || !activeConversation}
              />
              <Button
                ariaLabel={
                  isConversationClosed
                    ? t('conversationHeader:unlockConversation')
                    : t('conversationHeader:lockConversation')
                }
                icon={isConversationClosed ? 'lock-open' : 'lock'}
                round
                secondary
                onClick={() => {
                  const newStatus = isConversationClosed ? 'OPEN' : 'CLOSE';
                  updateConversationStatus(newStatus);
                }}
                disabled={isUpdatingStatus || !activeConversation}
              />
            </ButtonContainer>
          </Layout.Item>
        </Layout.Item>
      </Layout>

      {isEditingTitle && activeConversation ? (
        <div className="u-mb">
          <Select
            value={editableTitle}
            onChange={(e) => {
              setEditableTitle(e.target.value);
            }}
            disabled={isUpdatingTitle}
            className="u-mb-small"
          >
            {titleOptions?.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select>
          <Button
            onClick={async () => {
              if (editableTitle.trim()) {
                await updateConversationTitle(editableTitle.trim());
                setIsEditingTitle(false);
              }
            }}
            disabled={isUpdatingTitle}
          >
            {isUpdatingTitle ? t('conversationHeader:saving') : t('conversationHeader:save')}
          </Button>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
